import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { SITE } from "../lib/config";
import { useQuote } from "../components/site/Header";
import Breadcrumb from "../components/site/Breadcrumb";
import ProductCard from "../components/site/ProductCard";
import QuoteForm from "../components/site/QuoteForm";
import PPContainersContent from "../components/site/PPContainersContent";
import JuiceBottlesContent from "../components/site/JuiceBottlesContent";
import PaperCupsContent from "../components/site/PaperCupsContent";
import FoilContainersContent from "../components/site/FoilContainersContent";
import SipperGlassesContent from "../components/site/SipperGlassesContent";

const PP_SLUG = "pp-biriyani-containers";
const PP_SEO = {
  title: "Disposable PP Food Containers Wholesale in Chennai | Chennai Packs",
  description: "Disposable PP food containers for restaurants, cloud kitchens, caterers, takeaway and food delivery businesses. Multiple sizes available. Get a wholesale quote from Chennai Packs.",
  canonical: "https://chennaipacks.com/products/pp-biriyani-containers",
  h1: "Disposable PP Food Containers Wholesale in Chennai",
};

const JUICE_SLUG = "juice-pet-bottles";
const JUICE_SEO = {
  title: "Juice PET Bottles Wholesale in Chennai | Chennai Packs",
  description: "Food-grade juice PET bottles for juice shops, cold-pressed juice brands, cafes and beverage businesses. Square, round, sugarcane & star shapes. Get a wholesale quote from Chennai Packs.",
  canonical: "https://chennaipacks.com/products/juice-pet-bottles",
  h1: "Juice PET Bottles Wholesale in Chennai",
};

const PAPER_CUPS_SLUG = "paper-cups";
const PAPER_CUPS_SEO = {
  title: "Paper Cups Wholesale in Chennai | Tea, Coffee & Beverage Cups | Chennai Packs",
  description: "Disposable paper cups (60 ml – 350 ml) for tea shops, coffee shops, cafes, cloud kitchens and beverage brands. Food-grade, leak-proof, custom-print options. Get a wholesale quote from Chennai Packs.",
  canonical: "https://chennaipacks.com/products/paper-cups",
  h1: "Disposable Paper Cups Wholesale in Chennai",
};

const FOIL_SLUG = "aluminium-foil-containers";
const FOIL_SEO = {
  title: "Aluminium Foil Containers Wholesale in Chennai | Biryani Foil Boxes | Chennai Packs",
  description: "Oven-safe aluminium foil containers (No. 83, 204, 450, 660, 750, 830) for biryani, curries, takeaway and delivery. Board & foil lid options. Get a wholesale quote from Chennai Packs.",
  canonical: "https://chennaipacks.com/products/aluminium-foil-containers",
  h1: "Aluminium Foil Containers Wholesale in Chennai",
};

const SIPPER_SLUG = "sipper-glasses";
const SIPPER_SEO = {
  title: "Sipper Glasses Wholesale in Chennai | Milkshake & Smoothie Cups | Chennai Packs",
  description: "Crystal-clear sipper glasses (200 ml – 500 ml) for juice bars, cafes, milkshake shops, ice-cream brands and cloud kitchens. Dome, flat & straw-hole lid options. Get a wholesale quote from Chennai Packs.",
  canonical: "https://chennaipacks.com/products/sipper-glasses",
  h1: "Sipper Glasses Wholesale in Chennai",
};

const SEO_BY_SLUG = {
  [PP_SLUG]: PP_SEO,
  [JUICE_SLUG]: JUICE_SEO,
  [PAPER_CUPS_SLUG]: PAPER_CUPS_SEO,
  [FOIL_SLUG]: FOIL_SEO,
  [SIPPER_SLUG]: SIPPER_SEO,
};

function setMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

// Old slugs that were renamed — redirect to the new slug so existing Google/bookmark URLs keep working.
const SLUG_REDIRECTS = {
  "paper-meal-box--containers": "paper-meal-box-containers",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const openQuote = useQuote();
  const [data, setData] = useState(null);

  useEffect(() => {
    const redirectTo = SLUG_REDIRECTS[slug];
    if (redirectTo) {
      navigate(`/products/${redirectTo}`, { replace: true });
      return;
    }
    setData(null);
    api.product(slug).then(setData).catch(() => setData("error"));
  }, [slug, navigate]);

  // SEO overrides for custom landing pages
  useEffect(() => {
    const seo = SEO_BY_SLUG[slug];
    if (!seo) return;
    const prevTitle = document.title;
    document.title = seo.title;
    setMeta("description", seo.description);
    setCanonical(seo.canonical);
    return () => { document.title = prevTitle; };
  }, [slug]);

  if (data === "error")
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center">
        <h1 className="font-head text-3xl text-[#0A1C0E]">Product not found</h1>
        <Link to="/products" className="text-[#1D6B2A] font-semibold mt-4 inline-block">
          Back to products
        </Link>
      </div>
    );

  if (!data) return <div className="py-40 text-center text-[#3A4D3E]">Loading...</div>;

  const { product, related } = data;

  return (
    <div>
      <section className="bg-[#F8FAF8] border-b border-[#E2E8E4] pt-12 pb-14">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumb
            items={[
              { label: "Products", to: "/products" },
              { label: product.category_name, to: `/products?category=${product.category_slug}` },
              { label: product.name },
            ]}
          />
          <div className="mt-8 grid lg:grid-cols-12 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-6 rounded-[2rem] overflow-hidden aspect-square shadow-xl"
            >
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            </motion.div>

            <div className="lg:col-span-6">
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A]">
                {product.category_name}
              </span>
              <h1 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E] mt-3">
                {SEO_BY_SLUG[slug]?.h1 || product.name}
              </h1>
              <p className="text-lg text-[#3A4D3E] mt-4 leading-relaxed">{product.description}</p>

              <ul className="mt-7 space-y-3">
                {product.specs.map((s) => (
                  <li key={s} className="flex items-center gap-3">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-[#4CAF50]/15 text-[#1D6B2A] shrink-0">
                      <Check size={14} />
                    </span>
                    <span className="text-[#0A1C0E]">{s}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 inline-flex items-center gap-2 bg-white border border-[#E2E8E4] rounded-full px-4 py-2 text-sm">
                <span className="text-[#3A4D3E]">MOQ:</span>
                <span className="font-semibold text-[#0A1C0E]">{product.moq}</span>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  onClick={() => openQuote({ name: product.name, sku: product.sku })}
                  data-testid="detail-quote-btn"
                  className="bg-[#1D6B2A] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#154d1e] transition-colors flex items-center gap-2"
                >
                  Get a Quote <ArrowRight size={18} />
                </button>
                <a
                  href={`https://wa.me/${SITE.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#1D6B2A] text-[#1D6B2A] rounded-full px-8 py-4 font-semibold hover:bg-white transition-colors"
                >
                  Enquire on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {slug === PP_SLUG && <PPContainersContent />}
      {slug === JUICE_SLUG && <JuiceBottlesContent />}
      {slug === PAPER_CUPS_SLUG && <PaperCupsContent />}
      {slug === FOIL_SLUG && <FoilContainersContent />}
      {slug === SIPPER_SLUG && <SipperGlassesContent />}

      {/* Inline quote */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center mb-8">
            <h2 className="font-head text-3xl font-medium text-[#0A1C0E]">
              Request pricing for {product.name}
            </h2>
            <p className="text-[#3A4D3E] mt-2">Fill in the form and we will respond with bulk rates.</p>
          </div>
          <div className="bg-white border border-[#E2E8E4] rounded-3xl p-6 md:p-8 shadow-sm">
            <QuoteForm defaultProduct={product.name} defaultSku={product.sku} />
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-[#F8FAF8]">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <h2 className="font-head text-3xl font-medium text-[#0A1C0E] mb-8">Related products</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
